import PDFDocument from "pdfkit/js/pdfkit.standalone";
import { formatCnpjCpf, formatPrice } from "../../utils";

function textoSeguro(valor) {
  return String(valor ?? "").trim();
}

function numeroSeguro(valor) {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
}

function formatarData(data) {
  if (!data) return "";

  const dataFormatada = new Date(data);
  if (Number.isNaN(dataFormatada.getTime())) return "";

  return dataFormatada.toLocaleDateString("pt-BR");
}

function obterDescricaoPagamento(pagamento, meioPagamentoList) {
  if (pagamento?.meioPagamento?.descricao) {
    return pagamento.meioPagamento.descricao;
  }

  if (pagamento?.meioPagamentoSelecionado?.descricao) {
    return pagamento.meioPagamentoSelecionado.descricao;
  }

  const meioPagamento = meioPagamentoList.find(
    (item) => Number(item.id) === Number(pagamento?.meioPagamentoId),
  );

  return meioPagamento?.descricao || "";
}

function totalItensLista(listaProdutos) {
  if (!listaProdutos?.length) return 0;

  return listaProdutos.reduce((total, item) => {
    const subtotal =
      item?.subtotal !== undefined &&
      item?.subtotal !== null &&
      item?.subtotal !== ""
        ? numeroSeguro(item.subtotal)
        : numeroSeguro(item.quantidade) * numeroSeguro(item.valor);

    return total + subtotal;
  }, 0);
}

function totalVenda(dto) {
  const freteNota = dto?.freteCompoeNota ? numeroSeguro(dto?.frete) : 0;
  const subtotalProdutos = totalItensLista(dto?.nfSaidaItens || []);
  const subtotalDesconto = numeroSeguro(dto?.desconto);
  const subtotalAcrescimo = numeroSeguro(dto?.acrescimo);
  const subtotalDespesas = numeroSeguro(dto?.despesas);

  return {
    subTotal: subtotalProdutos,
    total:
      subtotalProdutos -
      subtotalDesconto +
      subtotalAcrescimo +
      subtotalDespesas +
      freteNota,
  };
}

function concatenarBytes(chunks) {
  const tamanhoTotal = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const resultado = new Uint8Array(tamanhoTotal);
  let offset = 0;

  chunks.forEach((chunk) => {
    resultado.set(chunk, offset);
    offset += chunk.length;
  });

  return resultado;
}

function bytesParaBase64(bytes) {
  let binario = "";
  const tamanhoFatia = 0x8000;

  for (let indice = 0; indice < bytes.length; indice += tamanhoFatia) {
    const fatia = bytes.subarray(indice, indice + tamanhoFatia);
    binario += String.fromCharCode(...fatia);
  }

  return btoa(binario);
}

async function carregarLogoBuffer(dataUrl) {
  if (!dataUrl) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const pngDataUrl = canvas.toDataURL("image/png");
        const base64 = pngDataUrl.split(",")[1];
        const binario = atob(base64);
        const bytes = new Uint8Array(binario.length);
        for (let i = 0; i < binario.length; i++) {
          bytes[i] = binario.charCodeAt(i);
        }
        resolve(bytes.buffer);
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

function mapearParaDto({ pdvEntity, dadosEmpresa, meioPagamentoList }) {
  const clienteNome =
    pdvEntity?.clienteNome ||
    pdvEntity?.clienteSelecionado?.nomeRazaoSocial ||
    "";
  const clienteCnpjCpf =
    pdvEntity?.clienteCnpjCpf || pdvEntity?.clienteSelecionado?.cnpjCpf || "";

  return {
    fantasia: textoSeguro(
      dadosEmpresa?.fantasia || dadosEmpresa?.nomeRazaoSocial,
    ),
    cnpjCpf: textoSeguro(dadosEmpresa?.cnpjCpf),
    enderecoLogradouro: textoSeguro(dadosEmpresa?.enderecoLogradouro),
    enderecoLogradouroNumero: textoSeguro(
      dadosEmpresa?.enderecoLogradouroNumero,
    ),
    enderecoCidade: textoSeguro(dadosEmpresa?.enderecoCidade),
    enderecoUF: textoSeguro(dadosEmpresa?.enderecoUF || dadosEmpresa?.uf),
    dataSaida: pdvEntity?.dataSaida || pdvEntity?.dataEmissao,
    numeroDocumento: textoSeguro(pdvEntity?.numeroDocumento),
    serieDocumento: textoSeguro(pdvEntity?.serieDocumento),
    clienteNome: textoSeguro(clienteNome),
    clienteCnpjCpf: textoSeguro(clienteCnpjCpf),
    freteCompoeNota: Boolean(pdvEntity?.freteCompoeNota),
    frete: numeroSeguro(pdvEntity?.frete),
    desconto: numeroSeguro(pdvEntity?.desconto),
    acrescimo: numeroSeguro(pdvEntity?.acrescimo),
    despesas: numeroSeguro(pdvEntity?.despesas),
    nfSaidaItens: (pdvEntity?.nfSaidaItens || []).map((item) => ({
      codigo: textoSeguro(item?.codigo),
      descricao: textoSeguro(item?.descricao),
      quantidade: numeroSeguro(item?.quantidade),
      unidadeCom: textoSeguro(item?.unidadeCom || item?.unidade || "UN"),
      valor: numeroSeguro(item?.valor),
      subtotal:
        item?.subtotal !== undefined &&
        item?.subtotal !== null &&
        item?.subtotal !== ""
          ? numeroSeguro(item.subtotal)
          : numeroSeguro(item?.quantidade) * numeroSeguro(item?.valor),
    })),
    nfSaidaPagamentos: (pdvEntity?.nfSaidaPagamentos || []).map(
      (pagamento) => ({
        valorTitulo: numeroSeguro(pagamento?.valorTitulo),
        descricao: textoSeguro(
          obterDescricaoPagamento(pagamento, meioPagamentoList),
        ),
      }),
    ),
  };
}

export async function generateDanfeNfceSecundaria({
  pdvEntity,
  dadosEmpresa,
  meioPagamentoList = [],
  imagemLogoBase64,
}) {
  const dto = mapearParaDto({ pdvEntity, dadosEmpresa, meioPagamentoList });
  const logoBuffer = await carregarLogoBuffer(imagemLogoBase64);
  const quantidadeItens = dto.nfSaidaItens.length;
  const quantidadePagamentos = dto.nfSaidaPagamentos.length;
  const comprimentoSemImagem =
    300 + 27 * quantidadeItens + 20 * quantidadePagamentos;
  const comprimento = logoBuffer
    ? comprimentoSemImagem + 20
    : comprimentoSemImagem;

  return new Promise((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: [203.77, comprimento],
        margin: 0,
        bufferPages: true,
        autoFirstPage: true,
        info: {
          Title: `DANFE - ${dto.numeroDocumento}`,
          Author: "SIAF Evolution",
          Subject: "Documento Auxiliar da Nota Fiscal Eletrônica",
          Keywords: "danfe, nfe, documento fiscal",
          Creator: "SIAF Evolution",
          Producer: "SIAF Evolution",
        },
      });

      const chunks = [];
      doc.on("data", (chunk) => {
        chunks.push(
          chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk),
        );
      });
      doc.on("error", (error) => reject(error));
      doc.on("end", () => {
        try {
          resolve(bytesParaBase64(concatenarBytes(chunks)));
        } catch (error) {
          reject(error);
        }
      });

      const margemEsquerda = 1;
      const margemTopo = 10;
      const margemDireita = doc?.page?.width - 1;
      const ajusteX = 2;
      const largura = 200;
      let inicio = 0;
      let inicioItem = 80;
      let inicioResumoTotais = 120;

      function linhaHorizontal(x1, x2, y) {
        const yPosicao = margemTopo + y;
        const xInicial = margemEsquerda + x1;
        const xFinal = margemDireita + x2;
        doc.moveTo(xInicial, yPosicao).lineTo(xFinal, yPosicao).stroke();
      }

      function campo(
        texto,
        x,
        y,
        larguraTexto = 100,
        alinhamento = "center",
        tamanho = 6,
      ) {
        doc
          .font("Helvetica-Bold")
          .fillColor("black")
          .fontSize(tamanho)
          .text(texto || "", margemEsquerda + x + ajusteX, margemTopo + y, {
            width: larguraTexto,
            align: alinhamento,
          });
      }

      function normal(
        texto,
        x,
        y,
        larguraTexto = 100,
        alinhamento = "center",
        tamanho = 6,
      ) {
        doc
          .font("Helvetica")
          .fillColor("black")
          .fontSize(tamanho)
          .text(texto || "", margemEsquerda + x + ajusteX, margemTopo + y, {
            width: larguraTexto,
            align: alinhamento,
            lineGap: -1,
          });
      }

      function desenhaCabecalho() {
        const enderecoCompleto = `${dto.enderecoLogradouro}, ${dto.enderecoLogradouroNumero}, ${dto.enderecoCidade} / ${dto.enderecoUF}`;

        normal(dto.fantasia, 1, inicio, largura, "center", 6);
        campo(formatCnpjCpf(dto.cnpjCpf), 1, inicio + 7, largura, "center", 6);
        campo(enderecoCompleto, 1, inicio + 14, largura, "center", 6);
        campo(
          `NF-SAIDA Nº${dto.numeroDocumento} SÉRIE: ${dto.serieDocumento}`,
          1,
          inicio + 21,
          largura,
          "center",
          6,
        );
        normal(
          formatarData(dto.dataSaida),
          1,
          inicio + 28,
          largura,
          "center",
          6,
        );
        campo("DADOS DO CLIENTE", 1, inicio + 40, largura, "center", 7);
        normal(dto.clienteNome.slice(0, 50), 1, inicio + 47, largura, "center", 6);
        normal(
          formatCnpjCpf(dto.clienteCnpjCpf),
          1,
          inicio + 54,
          largura,
          "center",
          6,
        );
      }

      function desenhaCabecalhoTabelaItens() {
        campo("CÓD", 3, inicio + 70, largura, "left", 7);
        campo("DESC", 30, inicio + 70, largura, "left", 7);
        campo("QTD", 82, inicio + 70, largura, "left", 7);
        campo("UN", 110, inicio + 70, largura, "left", 7);
        campo("UNIT", 140, inicio + 70, largura, "left", 7);
        campo("TOTAL", 172, inicio + 70, largura, "left", 7);
        linhaHorizontal(0, 0, inicio + 80);
      }

      function desenhaItem(produto) {
        const totalProduto =
          produto?.subtotal !== undefined
            ? numeroSeguro(produto.subtotal)
            : numeroSeguro(produto.valor) * numeroSeguro(produto.quantidade);

        if (produto.codigo) {
          normal(produto.codigo, 3, inicioItem + 5, largura, "left", 7);
        }

        normal(produto.descricao, 30, inicioItem + 5, largura, "left", 7);
        normal(
          formatPrice(numeroSeguro(produto.quantidade), false),
          72,
          inicioItem + 15,
          35,
          "center",
          7,
        );
        normal(produto.unidadeCom, 110, inicioItem + 15, 20, "left", 7);

        doc.font("Helvetica-Bold").fontSize(7);
        let larguraTexto = doc.widthOfString("UNIT");
        normal(
          formatPrice(numeroSeguro(produto.valor), false),
          125,
          inicioItem + 15,
          larguraTexto + 16,
          "right",
          7,
        );
        larguraTexto = doc.widthOfString("TOTAL");
        normal(
          formatPrice(totalProduto, false),
          150,
          inicioItem + 15,
          larguraTexto + 23,
          "right",
          7,
        );
        linhaHorizontal(0, 0, inicioItem + 23);
      }

      function desenhaResumoTotal(total, subTotal) {
        campo(
          "QTD. TOTAL DE ITENS",
          2,
          inicioResumoTotais,
          largura - 105,
          "left",
          7,
        );
        campo(
          String(dto.nfSaidaItens.length),
          105,
          inicioResumoTotais,
          largura - 110,
          "right",
          7,
        );
        campo("SUBTOTAL", 2, inicioResumoTotais + 10, largura - 105, "left", 7);
        campo(
          formatPrice(subTotal, false) || "0,00",
          105,
          inicioResumoTotais + 10,
          largura - 110,
          "right",
          7,
        );
        campo(
          "DESCONTOS",
          2,
          inicioResumoTotais + 20,
          largura - 105,
          "left",
          7,
        );
        campo(
          `- ${dto.desconto ? formatPrice(dto.desconto, false) : "0,00"}`,
          105,
          inicioResumoTotais + 20,
          largura - 110,
          "right",
          7,
        );
        campo(
          "ACRÉSCIMOS",
          2,
          inicioResumoTotais + 30,
          largura - 105,
          "left",
          7,
        );
        campo(
          `+ ${dto.despesas ? formatPrice(dto.despesas, false) : "0,00"}`,
          105,
          inicioResumoTotais + 30,
          largura - 110,
          "right",
          7,
        );
        campo(
          "VALOR A PAGAR",
          2,
          inicioResumoTotais + 40,
          largura - 105,
          "left",
          7,
        );
        campo(
          formatPrice(total, false) || "0,00",
          105,
          inicioResumoTotais + 40,
          largura - 110,
          "right",
          7,
        );
      }

      function desenharPagamentos(total) {
        let inicioPagamentos = inicioResumoTotais + 60;
        let valorPago = 0;

        campo("PAGAMENTOS", 1, inicioPagamentos, largura, "center", 7);
        inicioPagamentos += 10;

        dto.nfSaidaPagamentos.forEach((pagamento) => {
          campo(
            pagamento.descricao || "",
            2,
            inicioPagamentos,
            largura - 105,
            "left",
            7,
          );
          campo(
            formatPrice(pagamento.valorTitulo, false) || "0,00",
            105,
            inicioPagamentos,
            largura - 110,
            "right",
            7,
          );
          valorPago += numeroSeguro(pagamento.valorTitulo);
          inicioPagamentos += 10;
        });

        inicioPagamentos += 5;
        campo("VALOR RECEBIDO", 2, inicioPagamentos, largura - 105, "left", 7);
        campo(
          formatPrice(valorPago, false) || "0,00",
          105,
          inicioPagamentos,
          largura - 110,
          "right",
          7,
        );
        inicioPagamentos += 10;
        campo("TROCO", 2, inicioPagamentos, largura - 105, "left", 7);
        campo(
          formatPrice(valorPago - total, false) || "0,00",
          105,
          inicioPagamentos,
          largura - 110,
          "right",
          7,
        );
        return inicioPagamentos;
      }

      function desenhaLogo() {
        if (!logoBuffer) return;
        try {
          doc.image(logoBuffer, 65, 5, {
            fit: [80, 80],
            align: "center",
            valign: "center",
          });
          inicio = 80;
          inicioItem = inicioItem + inicio;
          inicioResumoTotais = inicioResumoTotais + inicio;
        } catch (e) {
          return;
        }
      }

      desenhaLogo();
      desenhaCabecalho();
      desenhaCabecalhoTabelaItens();

      for (let index = 0; index < quantidadeItens; index += 1) {
        if (index > 0) {
          inicioItem += 23;
          inicioResumoTotais += 23;
        }

        desenhaItem(dto.nfSaidaItens[index]);
      }
      const { total, subTotal } = totalVenda(dto);
      desenhaResumoTotal(total, subTotal);
      const y = desenharPagamentos(total);
      linhaHorizontal(0, 0, y + 40);
      campo("ASSINATURA", 2, y + 45, largura, "center", 7);

      doc.end();
    } catch (error) {
      reject(error);
    }
  });
}
